import i18n from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { FlightResult, Bound } from 'ts/flight/interfaces/FlightSearch';
import { format } from 'date-fns';
import { formatPrice, getCurrency, getDaysDifference } from 'utils/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faSuitcase } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import Dropdown from 'components/shared/Dropdown';

export const searchModes = [
  { value: '1', label: 'flight.round_trip' },
  { value: '0', label: 'flight.one_way' },
  { value: '2', label: 'flight.multiple_destinations' },
];

export const personsCount = [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
];

export const cabinTypes = [
  { value: 0, label: 'flight.economy' },
  { value: 3, label: 'flight.economy_premium' },
  { value: 1, label: 'flight.business' },
];

export enum RoundTripMode {
  Default = 0,
  Outbound = 1,
  Inbound = 2,
}

export enum PassengerType {
  Adult = 'ADT',
  Senior = 'SRC',
  Child = 'CNN',
  Infant = 'INF',
  Youth = 'YTH',
}

export const roundTripModes = [
  { value: RoundTripMode.Default, label: 'flight.round_trip' },
  { value: RoundTripMode.Outbound, label: 'flight.outbound' },
  { value: RoundTripMode.Inbound, label: 'flight.inbound' },
];

export const getSortOptions = (dep: string, arr: string) => [
  { value: 'priceIncrease', label: i18n.t('sort.price_increase') },
  { value: 'priceDecrease', label: i18n.t('sort.price_decrease') },
  { value: 'airlineIncrease', label: i18n.t('sort.airline_increase') },
  { value: 'airlineDecrease', label: i18n.t('sort.airline_decrease') },
  { value: 'durationIncrease', label: i18n.t('sort.duration_increase') },
  { value: 'durationDecrease', label: i18n.t('sort.duration_decrease') },
  {
    value: 'departureOutboundIncrease',
    label: i18n.t('sort.departure_outbound_increase', { dep }),
  },
  {
    value: 'departureOutboundDecrease',
    label: i18n.t('sort.departure_outbound_decrease', { dep }),
  },
  {
    value: 'returningOutboundIncrease',
    label: i18n.t('sort.returning_outbound_increase', { arr }),
  },
  {
    value: 'returningOutboundDecrease',
    label: i18n.t('sort.returning_outbound_decrease', { arr }),
  },
  { value: 'departureInboundIncrease', label: i18n.t('sort.departure_inbound_increase', { arr }) },
  { value: 'departureInboundDecrease', label: i18n.t('sort.departure_inbound_decrease', { arr }) },
  { value: 'returningInboundIncrease', label: i18n.t('sort.returning_inbound_increase', { dep }) },
  { value: 'returningInboundDecrease', label: i18n.t('sort.returning_inbound_decrease', { dep }) },
];

export const getStopsOptions = (oneWay: boolean) => {
  if (oneWay) {
    return [
      { number: 0, label: 'filter.direct_flights' },
      { number: 1, label: 'filter.one_stop' },
      { number: 2, label: 'filter.two_or_more_stops' },
    ];
  } else {
    return [
      { number: 0, label: 'filter.direct_flights' },
      { number: 0.5, label: 'filter.direct_flights_outbound' },
      { number: 1, label: 'filter.one_stop' },
      { number: 2, label: 'filter.two_or_more_stops' },
    ];
  }
};

export const token = {
  flight: 'searchFlightToken',
  outbound: 'searchOutboundToken',
  inbound: 'searchInboundToken',
};

export const genderOptions = [
  { value: 'M', label: 'male' },
  { value: 'F', label: 'female' },
];

export const getGenderString = (input: string) => {
  if (input === 'M') {
    return i18n.t('passenger.male');
  } else if (input === 'F') {
    return i18n.t('passenger.female');
  }
};

export const getFlightPassengerValidation = (index: number) => {
  const baseValidation = {
    firstName: { required: true, minLength: 2, maxLength: 20, english: true },
    lastName: { required: true, minLength: 2, maxLength: 20, english: true },
    dob: { required: true },
    phone: {},
    email: {},
  };
  if (index === 0) {
    return {
      ...baseValidation,
      email: { required: true, email: true },
      phone: { required: true },
    };
  } else {
    return baseValidation;
  }
};


const columnHelper = createColumnHelper<FlightResult>();

export const columns = [
  columnHelper.accessor((row) => row.bounds[0].airlineCode, {
    header: 'Air.',
    cell: (info) => (
      <div>
        {info.row.original.bounds.map((bound, i) => (
          <div key={i}>{bound.airlineCode}</div>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor((row) => format(new Date(row.bounds[0].departureDateTime), 'dd/MM'), {
    id: 'dep',
    header: 'Dep.',
    cell: (info) => (
      <div>
        {info.row.original.bounds.map((bound, i) => (
          <div key={i}>{format(new Date(bound.departureDateTime), 'dd/MM')}</div>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row.bounds[0].departureAirport.code, {
    id: 'f',
    header: 'F',
    cell: (info) => (
      <div>
        {info.row.original.bounds.map((bound, i) => (
          <div key={i}>{bound.departureAirport.code}</div>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row.bounds[0].arrivalAirport.code, {
    id: 't',
    header: 'T',
    cell: (info) => (
      <div>
        {info.row.original.bounds.map((bound, i) => (
          <div key={i}>{bound.arrivalAirport.code}</div>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor((row) => format(new Date(row.bounds[0].departureDateTime), 'HH:mm'), {
    id: 'departureTime',
    header: () => 'D',
    cell: (info) => (
      <div>
        {info.row.original.bounds.map((bound, i) => (
          <div key={i} className="text-bold">
            {format(new Date(bound.departureDateTime), 'HH:mm')}
          </div>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor((row) => format(new Date(row.bounds[0].arrivalDateTime), 'HH:mm'), {
    id: 'arrivalTime',
    header: () => 'A',
    cell: (info) => (
      <div>
        {info.row.original.bounds.map((bound, i) => (
          <div key={i} className="text-bold">
            {format(new Date(bound.arrivalDateTime), 'HH:mm')}
          </div>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row.bounds[0].durationTime, {
    id: 'dur',
    header: 'Dur.',
    cell: (info) => {
      const row = info.row.original;
      return (
        <div>
          {row.bounds.map((bound, i) => {
            const daysDifference = getDaysDifference(
              bound.departureDateTime,
              bound.arrivalDateTime
            );
            return (
              <div key={i} className="text-xsm" style={{ height: 22 }}>
                {bound.durationTime}
                {daysDifference > 0 && (
                  <span className="color-danger" style={{ fontSize: '0.6rem' }}>
                    {' '}
                    +{daysDifference}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row.bounds[0].availSeatsNo, {
    id: 's',
    header: 'S',
    cell: (info) => (
      <div>
        {info.row.original.bounds.map((bound, i) => (
          <div key={i} style={{ minHeight: 22 }}>
            {bound.stopAirports.map((code, j) => (
              <div key={j} className="text-xsm text-lh">
                {code}
              </div>
            ))}
            {i < info.row.original.bounds.length - 1 && bound.stopAirports.length > 0 && (
              <div className="mt-3 pt-3 bt-1" />
            )}
          </div>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor(
    (row) => {
      if (row.bounds[0].stopQuantity === 0) return 'Direct';
      return row.bounds[0].connTime || '-';
    },
    {
      id: 'maxConn',
      header: 'MaxConn.',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div>
            {row.bounds.map((bound, i) => {
              const value = bound.stopQuantity === 0 ? 'Direct' : bound.connTime || '-';
              return (
                <div key={i} className="text-xsm">
                  {value === 'Direct' || value === '-' ? (
                    value
                  ) : (
                    <span
                      className={
                        parseInt(value.split('h')[0]) > 5 ? 'text-xsm color-danger' : 'text-xsm'
                      }
                    >
                      {value}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    }
  ),
  columnHelper.accessor(
    (row) => {
      if (row.bounds[0].availSeatsNo === 0) return '';
      return `${row.bounds[0].class.code}${row.bounds[0].availSeatsNo}`;
    },
    {
      id: 'classSeats',
      header: '',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div>
            {row.bounds.map((bound, i) => {
              const value =
                bound.availSeatsNo === 0 ? '' : `${bound.class.code}${bound.availSeatsNo}`;
              return (
                <div
                  key={i}
                  className={`text-xsm${bound.availSeatsNo === 1 ? ' color-danger' : ''}`}
                >
                  {value}
                </div>
              );
            })}
          </div>
        );
      },
    }
  ),
  columnHelper.accessor((row) => row.bounds[0].cabin.desc, {
    id: 'cabinInfo',
    header: '',
    cell: (info) => {
      const row = info.row.original;

      return (
        <div>
          {row.bounds.map((bound, i) => (
            <div key={i} className="display-flex mt-5">
              <Tooltip content={`${bound.cabin.desc} [${row.flightSource}]`}>
                <div className="square mr-5" style={{ backgroundColor: 'var(--color-contrast)' }}>
                  {bound.cabin.code}
                </div>
              </Tooltip>
              {bound.fareFamilyDesc && (
                <Tooltip content={bound.fareFamilyDesc}>
                  <div className="square bg-dark">F</div>
                </Tooltip>
              )}
              {i < row.bounds.length - 1 && <div className="mt-5" />}
            </div>
          ))}
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row.bounds[0].listOfFlyBaggagePrice?.[0], {
    id: 'b',
    header: 'B',
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex-column">
          {row.bounds.map((bound, i) => {
            const baggage = bound.listOfFlyBaggagePrice?.[0];

            const getPieces = (bag: Bound['listOfFlyBaggagePrice'][0]) => {
              if (!bag) return '0';
              if (bag.pieces > 0 || bag.maxTotalWeight > 0) {
                if (bag.displayPrice.value > 0) {
                  return '$';
                } else {
                  return bag.pieces ? bag.pieces + '' : '1';
                }
              }
              return '0';
            };

            const pieces = getPieces(baggage);
            const tooltip =
              pieces === '$'
                ? `${formatPrice(
                  Math.round(baggage?.displayPrice?.value),
                  baggage?.displayPrice?.currency
                )}`
                : pieces === '0'
                  ? 'No baggage'
                  : 'Baggage';

            return (
              <div key={i} className="display-flex">
                <Tooltip content={tooltip}>
                  <span className="text-xsm mr-5">
                    <span className="mr-5">{pieces}</span>
                    <FontAwesomeIcon
                      icon={faSuitcase as any}
                      className={
                        (pieces === '0' ? 'color-danger' : '') +
                        (pieces === '$' ? 'text-warning' : '')
                      }
                    />
                  </span>
                </Tooltip>

                {bound.remark && (
                  <Tooltip
                    multiLine={true}
                    content={bound.remark}
                    children={<FontAwesomeIcon icon={faInfoCircle as any} />}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row.provider, {
    id: 'provider',
    header: '',
    cell: (info) => <span className="text-xsm">{info.getValue()}</span>,
  }),
  columnHelper.accessor(
    (row) => {
      return { flightSFM: row.flightSFM, flightPricesByPDT: row.flightPricesByPDT };
    },
    {
      id: 'price',
      header: 'Price',
      cell: (info) => {
        const { flightSFM, flightPricesByPDT } = info.getValue();
        return (
          <Dropdown
            appendToBody
            align="right"
            opener={
              <span className="text-bold text-md text-contrast">
                {getCurrency(flightSFM.price?.currency)}
                {Math.round(flightSFM.price?.value)?.toLocaleString()}
              </span>
            }
          >
            <div className="text-xsm table-default img-box-shadow">
              <table className="table-bordered-lines table-mobile">
                <thead>
                  <tr className="text-bold">
                    <th>PTC</th>
                    <th>Base</th>
                    <th>Taxes</th>
                    <th>SFM</th>
                    <th>Air. Comm.</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {flightPricesByPDT.map((price, index) => (
                    <tr key={index}>
                      <td>{price.paxPDT}</td>
                      <td>
                        {Math.round(price.gross.value)?.toLocaleString()} {price.gross.currency}
                      </td>
                      <td>
                        {Math.round(price.tax.value)?.toLocaleString()} {price.gross.currency}
                      </td>
                      <td>{ }</td>
                      <td>
                        {Math.round(price.agencyCommision.value)?.toLocaleString()}{' '}
                        {price.gross.currency}
                      </td>
                      <td>
                        {Math.round(price.grossAndTaxWithMarkup.value)?.toLocaleString()}{' '}
                        {price.gross.currency}
                      </td>
                    </tr>
                  ))}
                  <tr className="text-bold">
                    <td>Total</td>
                    <td>
                      {Math.round(flightSFM.priceNett.value)?.toLocaleString()}{' '}
                      {flightSFM.priceNett.currency}
                    </td>
                    <td>
                      {Math.round(flightSFM.priceTax.value)?.toLocaleString()}{' '}
                      {flightSFM.priceNett.currency}
                    </td>
                    <td>
                      {Math.round(flightSFM.priceProfit.value)?.toLocaleString()}{' '}
                      {flightSFM.priceNett.currency}
                    </td>
                    <td>
                      {Math.round(flightSFM.priceAirLineCommision.value)?.toLocaleString()}{' '}
                      {flightSFM.priceNett.currency}
                    </td>
                    <td>
                      {Math.round(flightSFM.price.value)?.toLocaleString()}{' '}
                      {flightSFM.priceNett.currency}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Dropdown>
        );
      },
    }
  ),
  columnHelper.accessor(
    (row) => {
      return { flightSFM: row.flightSFM };
    },
    {
      id: 'base',
      header: 'Base',
      cell: (info) => {
        const { flightSFM } = info.getValue();
        return <span>{Math.round(flightSFM.priceNett.value)?.toLocaleString()}</span>;
      },
    }
  ),
  columnHelper.accessor(
    (row) => {
      return { flightSFM: row.flightSFM };
    },
    {
      id: 'tax',
      header: 'Tax',
      cell: (info) => {
        const { flightSFM } = info.getValue();
        return <span>{Math.round(flightSFM.priceTax.value)?.toLocaleString()}</span>;
      },
    }
  ),
  columnHelper.accessor(
    (row) => {
      return { flightSFM: row.flightSFM };
    },
    {
      id: 'SFM',
      header: 'SFM',
      cell: (info) => {
        const { flightSFM } = info.getValue();
        return <span>{Math.round(flightSFM.priceProfit.value)?.toLocaleString()}</span>;
      },
    }
  ),
  columnHelper.accessor(
    (row) => {
      return { flightSFM: row.flightSFM };
    },
    {
      id: 'GDS',
      header: 'GDS',
      cell: (info) => {
        const { flightSFM } = info.getValue();
        return <span>{Math.round(flightSFM.priceAirLineCommision.value)?.toLocaleString()}</span>;
      },
    }
  ),
  columnHelper.accessor(
    (row) => {
      return { flightSFM: row.flightSFM };
    },
    {
      id: 'profit',
      header: 'Prof.',
      cell: (info) => {
        const { flightSFM } = info.getValue();
        return <span>{Math.round(flightSFM.priceProfit.value)?.toLocaleString()}</span>;
      },
    }
  ),
];
